import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  ArrowDownward as ArrowDownIcon,
  ArrowBack as ArrowLeftIcon,
  ArrowForward as ArrowRightIcon,
  ArrowUpward as ArrowUpIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import api from '../../api';
import {
  ESIGN_FIELD_TYPES,
  ESIGN_MERGE_KEYS,
  DEFAULT_CHECKBOX_LABEL,
  fieldTypeMeta,
  mergeKeyMeta,
  newFieldId,
  normalizeEsignField,
  pdfRectToScreen,
  screenRectToPdf,
  fetchDocumentEsignFields,
  saveDocumentEsignFields,
  isMergeField
} from '../../utils/esignFields';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const FIELD_COLORS = {
  signature: '#1b5e20',
  initials: '#0d47a1',
  dateSigned: '#e65100',
  name: '#00695c',
  text: '#37474f',
  checkbox: '#455a64',
  merge: '#6a1b9a'
};

/** Distinct hues so Tenant vs Tenant 2 boxes don’t look identical. */
const ASSIGNEE_COLORS = [
  '#c62828',
  '#1565c0',
  '#2e7d32',
  '#6a1b9a',
  '#00838f',
  '#ef6c00',
  '#4527a0',
  '#ad1457'
];

function colorForField(field, signers = []) {
  if (isMergeField(field)) return FIELD_COLORS.merge;
  const email = String(field.signerEmail || '')
    .trim()
    .toLowerCase();
  if (email) {
    const idx = Math.abs(
      [...email].reduce((acc, ch) => acc + ch.charCodeAt(0), 0)
    ) % ASSIGNEE_COLORS.length;
    return ASSIGNEE_COLORS[idx];
  }
  const role = String(field.role || '').trim();
  if (role) {
    const known = signers
      .map((s) => String(s.role || '').trim())
      .filter(Boolean)
      .filter((r, i, arr) => arr.indexOf(r) === i);
    const roleIdx = known.indexOf(role);
    if (roleIdx >= 0) return ASSIGNEE_COLORS[roleIdx % ASSIGNEE_COLORS.length];
    const idx =
      Math.abs([...role].reduce((acc, ch) => acc + ch.charCodeAt(0), 0)) %
      ASSIGNEE_COLORS.length;
    return ASSIGNEE_COLORS[idx];
  }
  return FIELD_COLORS[field.type] || FIELD_COLORS.text;
}

function typeDisplayLabel(type) {
  if (type === 'merge') return 'Merge';
  return fieldTypeMeta(type).label || type || 'Field';
}

function whoForField(field, signers = []) {
  const email = String(field.signerEmail || '').trim();
  if (email) {
    const match = signers.find(
      (s) => String(s.email || '').trim().toLowerCase() === email.toLowerCase()
    );
    if (match?.name) return match.name;
    if (match?.role) return match.role;
    return email.split('@')[0];
  }
  const role = String(field.role || '').trim();
  return role || '';
}

/** Overlay / list label: type first so truncated mobile boxes still show purpose. */
function labelForField(field, signers = []) {
  if (isMergeField(field)) {
    const key = field.mergeKey || field.label || 'data';
    return `Merge · ${key}`;
  }
  const typeLabel = typeDisplayLabel(field.type);
  const who = whoForField(field, signers);
  if (who) return `${typeLabel} · ${who}`;
  return typeLabel;
}

function fieldsSnapshot(list) {
  try {
    return JSON.stringify(
      (list || [])
        .map((f) => ({
          id: f.id,
          type: f.type,
          pageIndex: f.pageIndex,
          x: f.x,
          y: f.y,
          width: f.width,
          height: f.height,
          role: f.role || '',
          signerEmail: f.signerEmail || '',
          label: f.label || '',
          mergeKey: f.mergeKey || ''
        }))
        .sort((a, b) => String(a.id).localeCompare(String(b.id)))
    );
  } catch {
    return '[]';
  }
}

async function loadPdfBytesForDocument(doc) {
  if (doc?.id) {
    const res = await api.get(`/api/documents/${doc.id}/file`, {
      responseType: 'arraybuffer',
      timeout: 120000
    });
    const bytes = res.data;
    if (!bytes || bytes.byteLength < 100) {
      throw new Error('PDF download was empty or truncated');
    }
    return bytes;
  }
  if (doc?.fileUrl) {
    const res = await fetch(doc.fileUrl, { method: 'GET', cache: 'no-store' });
    if (!res.ok) throw new Error(`Unexpected server response (${res.status}) while retrieving PDF`);
    return res.arrayBuffer();
  }
  throw new Error('Missing PDF');
}

function canvasToObjectUrl(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Could not encode page preview'));
          return;
        }
        resolve(URL.createObjectURL(blob));
      },
      'image/jpeg',
      0.85
    );
  });
}

const DEFAULT_ROLES = ['Tenant', 'Tenant 2', 'Landlord', 'Witness', 'Co-tenant', 'Signer'];

function tenantToPerson(tenant, role = 'Tenant') {
  if (!tenant) return null;
  const name =
    [tenant.firstName, tenant.lastName].filter(Boolean).join(' ').trim() ||
    tenant.name ||
    tenant.fullName ||
    tenant.displayName ||
    '';
  const email = String(tenant.email || '').trim();
  return {
    name: name || (email ? email.split('@')[0] : role),
    email,
    role
  };
}

function normalizePeople(list = []) {
  return (list || [])
    .map((s) => ({
      name: String(s.name || '').trim(),
      email: String(s.email || '').trim(),
      role: String(s.role || 'Signer').trim() || 'Signer'
    }))
    .filter((s) => s.name || s.email || s.role);
}

/**
 * Place signature/initials/date/name/text/merge boxes on a landlord PDF
 * (ZRF-ESIGN-02 + ZRF-ESIGN-03). Coordinates saved as pdf-lib points (origin bottom-left).
 *
 * All pages stack and scroll with the dialog (no single-page-only canvas).
 */
const PdfFieldPlacer = ({
  open,
  onClose,
  document: doc,
  signers = [],
  tenants = [],
  onSignersChange,
  onSaved,
  onRequestSignatures
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const pageRefs = useRef({});
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const suppressPlaceRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [apiHint, setApiHint] = useState('');
  const [pages, setPages] = useState([]);
  /** CSS zoom only — pages render once at RENDER_SCALE. */
  const [zoom, setZoom] = useState(1);
  const [fields, setFields] = useState([]);
  const [activeType, setActiveType] = useState('signature');
  const [activeMergeKey, setActiveMergeKey] = useState('tenant.name');
  const [selectedId, setSelectedId] = useState(null);
  const [assignRole, setAssignRole] = useState('Tenant');
  const [assignEmail, setAssignEmail] = useState('');
  const [people, setPeople] = useState([]);
  const [newPerson, setNewPerson] = useState({ name: '', email: '', role: 'Tenant' });
  const [placeHint, setPlaceHint] = useState('');
  const [savedSnapshot, setSavedSnapshot] = useState('[]');
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [showPeoplePanel, setShowPeoplePanel] = useState(false);

  const RENDER_SCALE = 1.35;
  const fileUrl = doc?.fileUrl || '';
  const hasAttach = Boolean(doc?.propertyId || doc?.tenantId || doc?.leaseId);
  const canLoadPdf = Boolean(doc?.id || fileUrl);
  const canSave = Boolean(doc?.id);

  const pushPeople = (next) => {
    const normalized = normalizePeople(next);
    setPeople(normalized);
    if (onSignersChange) onSignersChange(normalized);
  };

  useEffect(() => {
    if (!open || !canLoadPdf) return undefined;
    let cancelled = false;
    const objectUrls = [];

    const load = async () => {
      setLoading(true);
      setError('');
      setApiHint('');
      setPlaceHint('');
      setSelectedId(null);
      setPages([]);
      setProgress('Downloading PDF…');
      try {
        const bytes = await loadPdfBytesForDocument(doc);
        if (cancelled) return;

        setProgress(`Downloaded ${(bytes.byteLength / 1024 / 1024).toFixed(1)} MB — rendering…`);
        const loadingTask = pdfjsLib.getDocument({
          data: bytes,
          disableRange: true,
          disableStream: true,
          disableAutoFetch: true,
          isEvalSupported: false
        });
        const pdf = await loadingTask.promise;
        if (cancelled) return;

        const nextPages = [];
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
          if (cancelled) return;
          setProgress(`Rendering page ${pageNum} of ${pdf.numPages}…`);
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: RENDER_SCALE });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          await page.render({ canvasContext: ctx, viewport }).promise;
          const src = await canvasToObjectUrl(canvas);
          objectUrls.push(src);
          nextPages.push({
            pageIndex: pageNum - 1,
            src,
            // pdf-lib / field coords use unscaled page points
            pageWidth: viewport.width / RENDER_SCALE,
            pageHeight: viewport.height / RENDER_SCALE,
            total: pdf.numPages
          });
          if (!cancelled) setPages([...nextPages]);
        }

        try {
          const existing = await fetchDocumentEsignFields(doc.id);
          if (!cancelled) {
            setFields(existing);
            setSavedSnapshot(fieldsSnapshot(existing));
          }
        } catch (err) {
          if (!cancelled) {
            setFields([]);
            setSavedSnapshot(fieldsSnapshot([]));
            if (err?.response?.status === 404) {
              setApiHint(
                'Could not load saved fields from API — placing locally until save succeeds.'
              );
            }
          }
        }
      } catch (err) {
        console.error('❌ PDF load failed:', err);
        if (!cancelled) {
          const msg = String(err?.message || err || '');
          setError(
            msg.includes('Missing PDF') || !canLoadPdf
              ? 'This document has no PDF to preview.'
              : `Could not load PDF preview (${msg}). Try again, or re-upload the PDF.`
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setProgress('');
        }
      }
    };

    load();
    return () => {
      cancelled = true;
      objectUrls.forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.id, fileUrl]);

  // Seed people from signers + attached tenant so For person is never empty
  useEffect(() => {
    if (!open) return;
    const seeded = normalizePeople(signers);
    if (doc?.tenantId && Array.isArray(tenants) && tenants.length) {
      const tenant = tenants.find((t) => t.id === doc.tenantId);
      const person = tenantToPerson(tenant, 'Tenant');
      if (person && (person.email || person.name)) {
        const exists = seeded.some(
          (s) =>
            (person.email && s.email.toLowerCase() === person.email.toLowerCase()) ||
            (s.role === 'Tenant' && s.name && person.name && s.name === person.name)
        );
        if (!exists) seeded.unshift(person);
      }
    }
    if (!seeded.length) {
      seeded.push({ name: '', email: '', role: 'Tenant' });
      seeded.push({ name: '', email: '', role: 'Tenant 2' });
    }
    setPeople(seeded);
    const withEmail = seeded.filter((s) => s.email);
    if (withEmail.length) {
      setAssignEmail(withEmail[0].email);
      setAssignRole(withEmail[0].role || 'Tenant');
    } else {
      setAssignEmail('');
      setAssignRole(seeded[0]?.role || 'Tenant');
    }
    // Only re-seed when dialog opens / document changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.id, doc?.tenantId]);

  useEffect(() => {
    if (!assignEmail) return;
    const match = people.find(
      (s) => s.email.toLowerCase() === String(assignEmail).trim().toLowerCase()
    );
    if (match?.role) setAssignRole(match.role);
  }, [assignEmail, people]);

  const activeAssigneeLabel = (() => {
    if (activeType === 'merge') {
      return `Merge · ${mergeKeyMeta(activeMergeKey)?.label || activeMergeKey || 'data'}`;
    }
    const typeLabel = typeDisplayLabel(activeType);
    const match = people.find(
      (s) => s.email && s.email.toLowerCase() === String(assignEmail || '').trim().toLowerCase()
    );
    const who =
      match?.name || match?.role || (assignEmail ? assignEmail.split('@')[0] : '') || assignRole;
    return who ? `${typeLabel} · ${who}` : typeLabel;
  })();

  const personSelectValue = assignEmail
    ? `email:${assignEmail}`
    : assignRole
      ? `role:${assignRole}`
      : '';

  const handlePersonSelect = (value) => {
    if (!value) {
      setAssignEmail('');
      setAssignRole('');
      return;
    }
    if (value.startsWith('email:')) {
      const email = value.slice(6);
      setAssignEmail(email);
      const match = people.find((s) => s.email === email);
      setAssignRole(match?.role || assignRole || 'Tenant');
      return;
    }
    if (value.startsWith('role:')) {
      setAssignEmail('');
      setAssignRole(value.slice(5));
    }
  };

  const addPerson = () => {
    const name = String(newPerson.name || '').trim();
    const email = String(newPerson.email || '').trim();
    const role = String(newPerson.role || 'Tenant').trim() || 'Tenant';
    if (!name && !email) {
      setError('Enter a name or email for the person.');
      return;
    }
    const next = normalizePeople([...people, { name, email, role }]);
    pushPeople(next);
    if (email) {
      setAssignEmail(email);
      setAssignRole(role);
    } else {
      setAssignEmail('');
      setAssignRole(role);
    }
    setNewPerson({ name: '', email: '', role: role === 'Tenant' ? 'Tenant 2' : 'Tenant' });
    setPlaceHint(`Added ${name || email || role}. Tap the page to place their boxes.`);
  };

  const activeLabel =
    activeType === 'merge'
      ? mergeKeyMeta(activeMergeKey)?.label || 'Merge data'
      : fieldTypeMeta(activeType).label;

  const handlePageClick = (e, page) => {
    // iOS often makes e.target a child / text node — do not require target === currentTarget
    if (e.target?.closest?.('[data-esign-field]')) return;
    if (suppressPlaceRef.current) {
      suppressPlaceRef.current = false;
      return;
    }
    if (activeType === 'merge' && !activeMergeKey) {
      setError('Choose a merge key before placing a merge field.');
      return;
    }
    if (activeType !== 'merge' && !assignEmail && !assignRole) {
      setError(
        'Choose who this box is for (Person email or Role) before placing — otherwise Tenant and Tenant 2 look the same.'
      );
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height || !page.pageWidth) {
      setError('Page preview is not ready yet — wait for pages to finish loading, then tap again.');
      return;
    }
    // Use on-screen size so placement stays correct when the page is CSS-scaled / responsive
    const effectiveScale = rect.width / page.pageWidth;
    const meta = fieldTypeMeta(activeType);
    const keyMeta = activeType === 'merge' ? mergeKeyMeta(activeMergeKey) : null;
    // Smaller defaults on phones so taps land nearer the intended line (wide boxes clamp to edges)
    const sizeMul = isMobile ? 0.72 : 1;
    const boxW = meta.defaultW * sizeMul;
    const boxH = meta.defaultH * sizeMul;
    const clientX = e.clientX ?? e.changedTouches?.[0]?.clientX;
    const clientY = e.clientY ?? e.changedTouches?.[0]?.clientY;
    if (clientX == null || clientY == null) return;
    const left = clientX - rect.left - boxW * effectiveScale * 0.5;
    const top = clientY - rect.top - boxH * effectiveScale * 0.5;
    const pdf = screenRectToPdf(
      {
        left: Math.max(0, left),
        top: Math.max(0, top),
        width: boxW * effectiveScale,
        height: boxH * effectiveScale
      },
      page.pageWidth,
      page.pageHeight,
      effectiveScale
    );
    const next = normalizeEsignField({
      id: newFieldId(),
      type: activeType,
      pageIndex: page.pageIndex,
      ...pdf,
      role: activeType === 'merge' ? '' : assignRole,
      signerEmail: activeType === 'merge' ? '' : assignEmail,
      // Prefer a clear checkbox prompt — bare "Checkbox" confuses signers on the public form
      label:
        activeType === 'merge'
          ? keyMeta?.label || meta.label
          : activeType === 'checkbox'
            ? DEFAULT_CHECKBOX_LABEL
            : meta.label,
      required: activeType !== 'merge',
      mergeKey: activeType === 'merge' ? activeMergeKey : undefined
    });
    setFields((prev) => [...prev, next]);
    setSelectedId(next.id);
    setError('');
    setPlaceHint(
      `Placed “${labelForField(next, people)}”. Drag to adjust, then Save.`
    );
  };

  const clampFieldOnPage = (field, pageWidth, pageHeight) => {
    const width = Math.max(24, Math.min(Number(field.width) || 24, pageWidth));
    const height = Math.max(16, Math.min(Number(field.height) || 16, pageHeight));
    const x = Math.max(0, Math.min(Number(field.x) || 0, pageWidth - width));
    const y = Math.max(0, Math.min(Number(field.y) || 0, pageHeight - height));
    return normalizeEsignField({ ...field, x, y, width, height });
  };

  const updateFieldById = (id, patchOrFn) => {
    setFields((prev) =>
      prev.map((f) => {
        if (f.id !== id) return f;
        const patch = typeof patchOrFn === 'function' ? patchOrFn(f) : patchOrFn;
        const page = pages.find((p) => p.pageIndex === f.pageIndex);
        const next = normalizeEsignField({ ...f, ...patch });
        if (!page) return next;
        return clampFieldOnPage(next, page.pageWidth, page.pageHeight);
      })
    );
  };

  const nudgeSelected = (dxPts, dyPts) => {
    if (!selectedId) return;
    updateFieldById(selectedId, (f) => ({
      x: (Number(f.x) || 0) + dxPts,
      // PDF origin is bottom-left: +dyPts moves up on the page
      y: (Number(f.y) || 0) + dyPts
    }));
    setPlaceHint('Moved field — Save when positioned correctly.');
  };

  const beginFieldDrag = (e, field, page) => {
    if (e.button != null && e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const overlay = e.currentTarget.parentElement;
    if (!overlay) return;
    const rect = overlay.getBoundingClientRect();
    if (!rect.width) return;
    setSelectedId(field.id);
    dragRef.current = {
      id: field.id,
      startClientX: e.clientX,
      startClientY: e.clientY,
      origX: Number(field.x) || 0,
      origY: Number(field.y) || 0,
      pageWidth: page.pageWidth,
      pageHeight: page.pageHeight,
      effectiveScale: rect.width / page.pageWidth,
      moved: false
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onFieldPointerMove = (e) => {
    const d = dragRef.current;
    const r = resizeRef.current;
    if (d) {
      const dxPx = e.clientX - d.startClientX;
      const dyPx = e.clientY - d.startClientY;
      if (Math.abs(dxPx) + Math.abs(dyPx) > 5) d.moved = true;
      const dx = dxPx / d.effectiveScale;
      const dy = dyPx / d.effectiveScale;
      updateFieldById(d.id, {
        x: d.origX + dx,
        y: d.origY - dy
      });
      return;
    }
    if (r) {
      const dxPx = e.clientX - r.startClientX;
      const dyPx = e.clientY - r.startClientY;
      if (Math.abs(dxPx) + Math.abs(dyPx) > 5) r.moved = true;
      const dw = dxPx / r.effectiveScale;
      const dh = dyPx / r.effectiveScale;
      updateFieldById(r.id, {
        width: r.origW + dw,
        // Growing down on screen shrinks bottom-left y unless we keep top edge fixed:
        // top = pageHeight - y - h; keep top fixed → y' = y - dh when height grows by dh
        height: r.origH + dh,
        y: r.origY - dh
      });
    }
  };

  const endFieldPointer = () => {
    if (dragRef.current?.moved || resizeRef.current?.moved) {
      suppressPlaceRef.current = true;
      setPlaceHint('Adjusted field — Save when positioned correctly.');
    }
    dragRef.current = null;
    resizeRef.current = null;
  };

  const beginResize = (e, field, page) => {
    if (e.button != null && e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const overlay = e.currentTarget.parentElement?.parentElement;
    if (!overlay) return;
    const rect = overlay.getBoundingClientRect();
    if (!rect.width) return;
    setSelectedId(field.id);
    resizeRef.current = {
      id: field.id,
      startClientX: e.clientX,
      startClientY: e.clientY,
      origW: Number(field.width) || 40,
      origH: Number(field.height) || 20,
      origY: Number(field.y) || 0,
      effectiveScale: rect.width / page.pageWidth,
      moved: false
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const updateSelected = (patch) => {
    if (!selectedId) return;
    updateFieldById(selectedId, patch);
  };

  const removeField = (id) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const jumpToPage = (pageIndex) => {
    const el = pageRefs.current[pageIndex];
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const isDirty = fieldsSnapshot(fields) !== savedSnapshot;

  const handleSave = async ({ closeAfter = false } = {}) => {
    if (!doc?.id) return false;
    if (fields.length === 0) {
      setError('Place at least one field before saving.');
      return false;
    }
    const unassigned = fields.filter(
      (f) => !isMergeField(f) && !String(f.signerEmail || '').trim() && !String(f.role || '').trim()
    );
    if (unassigned.length > 0) {
      setError(
        `${unassigned.length} box(es) have no person/role. Select each and set Role, or remove and re-place with For person chosen.`
      );
      return false;
    }
    setSaving(true);
    setError('');
    try {
      const saved = await saveDocumentEsignFields(doc.id, fields);
      setFields(saved);
      setSavedSnapshot(fieldsSnapshot(saved));
      setApiHint('');
      setPlaceHint(`Saved ${saved.length} field(s) on this document.`);
      if (onSaved) onSaved(saved);
      if (closeAfter && onClose) onClose();
      else if (!closeAfter) {
        setPlaceHint(
          `Saved ${saved.length} field(s). Keep editing — switch What to place to Merge data to add more, then Save again.`
        );
      }
      return true;
    } catch (err) {
      console.error('❌ Save esign fields failed:', err);
      const status = err?.response?.status;
      if (status === 404 || status === 501) {
        setApiHint(
          'Save API not ready (BE). Fields kept in this dialog — pass them on “Send for signature” body until BE lands.'
        );
        setSavedSnapshot(fieldsSnapshot(fields));
        if (onSaved) onSaved(fields);
        if (closeAfter && onClose) onClose();
        return true;
      }
      setError(err?.response?.data?.error || 'Failed to save field definitions');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const requestClose = () => {
    if (isDirty) {
      setCloseConfirmOpen(true);
      return;
    }
    if (onClose) onClose();
  };

  const handleContinue = async () => {
    if (!onRequestSignatures) return;
    if (fields.length === 0) {
      setError('Place at least one field before continuing.');
      return;
    }
    if (isDirty) {
      const ok = await handleSave({ closeAfter: false });
      if (!ok) return;
    }
    onRequestSignatures(fields);
  };

  const selected = fields.find((f) => f.id === selectedId);
  const pageCount = pages[0]?.total || pages.length;

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
    >
      <DialogTitle sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 1.25, sm: 2 }, pr: { xs: 1, sm: 3 } }}>
        <Typography variant={isMobile ? 'subtitle1' : 'h6'} component="div" fontWeight={600}>
          {fields.length > 0 ? 'Edit fields' : 'Place fields'}
        </Typography>
        {doc?.title ? (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ display: 'block', maxWidth: '100%' }}
          >
            {doc.title}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          px: { xs: 1.25, sm: 3 },
          py: { xs: 1.25, sm: 2 },
          overflowX: 'hidden'
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {apiHint && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {apiHint}
          </Alert>
        )}
        {!isMobile && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Fields are saved on the document when you tap <strong>Save fields</strong> — Close alone
            discards unsaved moves. PDF Preview still shows the original file; reopen Place fields (or
            send for signature) to see overlays.
          </Alert>
        )}
        {!isMobile && (
          <Alert severity={hasAttach ? 'info' : 'warning'} sx={{ mb: 2 }}>
            Attach property/tenant/lease so merge fields can fill.
            {!hasAttach &&
              ' This document is not attached yet — merge boxes will stamp blank until you attach.'}
          </Alert>
        )}
        {placeHint && (
          <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setPlaceHint('')}>
            {placeHint}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                bgcolor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
                pb: 1,
                mb: 1.5,
                maxWidth: '100%'
              }}
            >
              <Stack spacing={1}>
                <FormControl size="small" fullWidth sx={{ minWidth: 0 }}>
                  <InputLabel>What to place</InputLabel>
                  <Select
                    label="What to place"
                    value={activeType}
                    onChange={(e) => setActiveType(e.target.value)}
                  >
                    {ESIGN_FIELD_TYPES.map((t) => (
                      <MenuItem key={t.type} value={t.type}>
                        {t.type === 'merge' ? 'Merge data (auto-fill)' : t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {activeType === 'merge' ? (
                  <FormControl size="small" fullWidth sx={{ minWidth: 0 }}>
                    <InputLabel>Merge value</InputLabel>
                    <Select
                      label="Merge value"
                      value={activeMergeKey}
                      onChange={(e) => setActiveMergeKey(e.target.value)}
                    >
                      {ESIGN_MERGE_KEYS.map((k) => (
                        <MenuItem key={k.key} value={k.key}>
                          {k.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <FormControl size="small" fullWidth sx={{ minWidth: 0 }}>
                    <InputLabel>For person / role</InputLabel>
                    <Select
                      label="For person / role"
                      value={personSelectValue}
                      onChange={(e) => handlePersonSelect(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Choose who…</em>
                      </MenuItem>
                      {people
                        .filter((s) => s.email)
                        .map((s) => (
                          <MenuItem key={`email:${s.email}`} value={`email:${s.email}`}>
                            {s.name
                              ? `${s.name} (${s.role || 'Signer'})`
                              : `${s.role || 'Signer'} · ${s.email}`}
                          </MenuItem>
                        ))}
                      {DEFAULT_ROLES.map((role) => (
                        <MenuItem key={`role:${role}`} value={`role:${role}`}>
                          Role: {role}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Stack>

              {activeType !== 'merge' && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    fullWidth={isMobile}
                    startIcon={<PersonAddIcon />}
                    endIcon={showAddPerson ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setShowAddPerson((v) => !v)}
                  >
                    {showAddPerson ? 'Hide add person' : 'Add person (name / email)'}
                  </Button>
                  <Collapse in={showAddPerson}>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      {!people.some((p) => p.email) && (
                        <Alert severity="warning" sx={{ py: 0.5 }}>
                          Add an email so labels stay clear for Tenant vs Tenant 2.
                        </Alert>
                      )}
                      <TextField
                        size="small"
                        fullWidth
                        label="Name"
                        value={newPerson.name}
                        onChange={(e) => setNewPerson((p) => ({ ...p, name: e.target.value }))}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="Email"
                        type="email"
                        value={newPerson.email}
                        onChange={(e) => setNewPerson((p) => ({ ...p, email: e.target.value }))}
                      />
                      <FormControl size="small" fullWidth>
                        <InputLabel>Role</InputLabel>
                        <Select
                          label="Role"
                          value={newPerson.role}
                          onChange={(e) => setNewPerson((p) => ({ ...p, role: e.target.value }))}
                        >
                          {DEFAULT_ROLES.map((role) => (
                            <MenuItem key={role} value={role}>
                              {role}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button size="small" variant="contained" onClick={addPerson} fullWidth>
                        Add person
                      </Button>
                    </Stack>
                  </Collapse>
                </Box>
              )}

              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ mt: 1 }}
                flexWrap="wrap"
                useFlexGap
              >
                <Chip
                  size="small"
                  color={
                    activeType === 'merge' || assignEmail || assignRole ? 'primary' : 'warning'
                  }
                  label={
                    activeType === 'merge'
                      ? `Tap: ${activeLabel}`
                      : assignEmail || assignRole
                        ? `Tap: ${activeAssigneeLabel}`
                        : 'Choose who first'
                  }
                  sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                />
                <Typography variant="caption" color="text.secondary">
                  {pageCount ? `${pageCount}p` : '—'} · {fields.length}f
                </Typography>
                {!isMobile && (
                  <>
                    <Button
                      size="small"
                      onClick={() =>
                        setZoom((z) => Math.min(1.75, Math.round((z + 0.15) * 100) / 100))
                      }
                      disabled={loading}
                    >
                      Zoom +
                    </Button>
                    <Button
                      size="small"
                      onClick={() =>
                        setZoom((z) => Math.max(0.55, Math.round((z - 0.15) * 100) / 100))
                      }
                      disabled={loading}
                    >
                      Zoom −
                    </Button>
                  </>
                )}
              </Stack>
            </Box>

            {!isMobile && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Assign each signature/name box to a person (Tenant vs Tenant 2). Merge data is
                auto-fill from the attached tenant/property — not a signer.
              </Typography>
            )}

            {loading && pages.length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress size={28} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {progress || 'Loading PDF…'}
                </Typography>
              </Box>
            )}
            {loading && pages.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {progress}
              </Typography>
            )}

            <Stack spacing={2} sx={{ pb: 2 }}>
              {pages.map((page) => {
                const pageFields = fields.filter((f) => f.pageIndex === page.pageIndex);
                return (
                  <Box
                    key={page.pageIndex}
                    ref={(el) => {
                      pageRefs.current[page.pageIndex] = el;
                    }}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: '#eee',
                      overflow: 'visible',
                      maxWidth: '100%'
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        px: 1,
                        py: 0.5,
                        bgcolor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      Page {page.pageIndex + 1} of {page.total}
                    </Typography>
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: '100%',
                        mx: 'auto',
                        lineHeight: 0,
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        component="img"
                        src={page.src}
                        alt={`PDF page ${page.pageIndex + 1}`}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          userSelect: 'none',
                          pointerEvents: 'none'
                        }}
                      />
                      <Box
                        onClick={(e) => handlePageClick(e, page)}
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          cursor: 'crosshair',
                          bgcolor: 'rgba(46, 125, 50, 0.02)',
                          WebkitTapHighlightColor: 'rgba(46, 125, 50, 0.15)',
                          touchAction: 'manipulation'
                        }}
                      >
                        {pageFields.map((f) => {
                          const box = pdfRectToScreen(f, page.pageHeight, 1);
                          const color = colorForField(f, people);
                          const isSel = f.id === selectedId;
                          const boxLabel = labelForField(f, people);
                          return (
                            <Box
                              key={f.id}
                              data-esign-field={f.id}
                              onPointerDown={(ev) => beginFieldDrag(ev, f, page)}
                              onPointerMove={onFieldPointerMove}
                              onPointerUp={endFieldPointer}
                              onPointerCancel={endFieldPointer}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                setSelectedId(f.id);
                              }}
                              title={boxLabel}
                              sx={{
                                position: 'absolute',
                                left: `${(box.left / page.pageWidth) * 100}%`,
                                top: `${(box.top / page.pageHeight) * 100}%`,
                                width: `${(box.width / page.pageWidth) * 100}%`,
                                height: `${(box.height / page.pageHeight) * 100}%`,
                                minWidth: 36,
                                minHeight: 24,
                                border: `2px solid ${color}`,
                                bgcolor: isSel ? `${color}66` : `${color}40`,
                                borderRadius: 0.5,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                justifyContent: 'center',
                                fontSize: 9,
                                lineHeight: 1.1,
                                color: '#111',
                                fontWeight: 700,
                                userSelect: 'none',
                                boxSizing: 'border-box',
                                px: 0.4,
                                py: 0.15,
                                overflow: 'hidden',
                                zIndex: isSel ? 3 : 1,
                                pointerEvents: 'auto',
                                cursor: 'grab',
                                touchAction: 'none',
                                textAlign: 'left',
                                '&:active': { cursor: 'grabbing' }
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: '100%',
                                  pointerEvents: 'none',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {boxLabel}
                              </Box>
                              {isSel && (
                                <Box
                                  data-esign-resize={f.id}
                                  onPointerDown={(ev) => beginResize(ev, f, page)}
                                  onPointerMove={onFieldPointerMove}
                                  onPointerUp={endFieldPointer}
                                  onPointerCancel={endFieldPointer}
                                  sx={{
                                    position: 'absolute',
                                    right: -6,
                                    bottom: -6,
                                    width: 16,
                                    height: 16,
                                    borderRadius: '2px',
                                    bgcolor: color,
                                    border: '2px solid #fff',
                                    cursor: 'nwse-resize',
                                    touchAction: 'none',
                                    zIndex: 4
                                  }}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>

          <Box
            sx={{
              width: { xs: '100%', md: 280 },
              flexShrink: 0,
              position: { md: 'sticky' },
              top: { md: 0 },
              maxWidth: '100%'
            }}
          >
            {isMobile ? (
              <Button
                size="small"
                fullWidth
                endIcon={showPeoplePanel ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setShowPeoplePanel((v) => !v)}
                sx={{ mb: 1 }}
              >
                People & field list ({people.length} people · {fields.length} fields)
              </Button>
            ) : (
              <Typography variant="subtitle2" gutterBottom>
                People for this signing
              </Typography>
            )}
            <Collapse in={!isMobile || showPeoplePanel}>
              {!isMobile && (
                <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                  Add Tenant / Tenant 2 with emails here (or use Role: Tenant above). Sticky{' '}
                  <strong>For person / role</strong> drives labels and colors.
                </Alert>
              )}
              <Stack spacing={0.5} sx={{ mb: 2, maxHeight: 120, overflow: 'auto' }}>
                {people.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No people yet — add above.
                  </Typography>
                )}
                {people.map((s, idx) => (
                  <Chip
                    key={`${s.email || s.role}-${idx}`}
                    size="small"
                    label={
                      s.email
                        ? `${s.name || s.role} · ${s.email}`
                        : `${s.role}${s.name ? ` · ${s.name}` : ' (no email)'}`
                    }
                    onClick={() => {
                      if (s.email) {
                        setAssignEmail(s.email);
                        setAssignRole(s.role);
                      } else {
                        setAssignEmail('');
                        setAssignRole(s.role);
                      }
                    }}
                    onDelete={() => {
                      const next = people.filter((_, i) => i !== idx);
                      pushPeople(next);
                    }}
                    variant={
                      (s.email && s.email === assignEmail) ||
                      (!s.email && !assignEmail && s.role === assignRole)
                        ? 'filled'
                        : 'outlined'
                    }
                    color={
                      (s.email && s.email === assignEmail) ||
                      (!s.email && !assignEmail && s.role === assignRole)
                        ? 'primary'
                        : 'default'
                    }
                    sx={{ maxWidth: '100%', justifyContent: 'space-between' }}
                  />
                ))}
              </Stack>
              {!isMobile && (
                <>
                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <InputLabel>Person (email)</InputLabel>
                    <Select
                      label="Person (email)"
                      value={assignEmail}
                      onChange={(e) => setAssignEmail(e.target.value)}
                    >
                      <MenuItem value="">Role only</MenuItem>
                      {people
                        .filter((s) => s.email)
                        .map((s) => (
                          <MenuItem key={s.email} value={s.email}>
                            {s.name
                              ? `${s.name} · ${s.role || 'Signer'} · ${s.email}`
                              : `${s.role || 'Signer'} · ${s.email}`}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Role</InputLabel>
                    <Select
                      label="Role"
                      value={assignRole}
                      onChange={(e) => setAssignRole(e.target.value)}
                    >
                      <MenuItem value="">Choose role…</MenuItem>
                      {[...DEFAULT_ROLES, ...people.map((s) => s.role).filter(Boolean)]
                        .filter((role, i, arr) => arr.indexOf(role) === i)
                        .map((role) => (
                          <MenuItem key={role} value={role}>
                            {role}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </>
              )}

              {selected && !isMobile && (
                <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected field
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Label"
                    value={selected.label || ''}
                    onChange={(e) => updateSelected({ label: e.target.value })}
                    sx={{ mb: 1 }}
                  />
                  {isMergeField(selected) ? (
                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                      <InputLabel>Merge key</InputLabel>
                      <Select
                        label="Merge key"
                        value={selected.mergeKey || ''}
                        onChange={(e) => {
                          const key = e.target.value;
                          const meta = mergeKeyMeta(key);
                          updateSelected({
                            type: 'merge',
                            mergeKey: key,
                            label: meta?.label || selected.label,
                            required: false,
                            role: '',
                            signerEmail: ''
                          });
                        }}
                      >
                        {ESIGN_MERGE_KEYS.map((k) => (
                          <MenuItem key={k.key} value={k.key}>
                            {k.label} ({k.key})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                      <InputLabel>Role</InputLabel>
                      <Select
                        label="Role"
                        value={selected.role || ''}
                        onChange={(e) => updateSelected({ role: e.target.value })}
                      >
                        <MenuItem value="">Any role</MenuItem>
                        {DEFAULT_ROLES.concat(people.map((s) => s.role).filter(Boolean))
                          .filter((role, i, arr) => arr.indexOf(role) === i)
                          .map((role) => (
                            <MenuItem key={role} value={role}>
                              {role}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <TextField
                      size="small"
                      label="Width"
                      type="number"
                      value={Math.round(selected.width || 0)}
                      onChange={(e) => updateSelected({ width: Number(e.target.value) || 24 })}
                      inputProps={{ min: 24, step: 4 }}
                    />
                    <TextField
                      size="small"
                      label="Height"
                      type="number"
                      value={Math.round(selected.height || 0)}
                      onChange={(e) => updateSelected({ height: Number(e.target.value) || 16 })}
                      inputProps={{ min: 16, step: 4 }}
                    />
                  </Stack>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => removeField(selected.id)}
                  >
                    Remove
                  </Button>
                </Box>
              )}

              <Typography variant="subtitle2" gutterBottom>
                All fields ({fields.length})
              </Typography>
              <Stack spacing={0.5} sx={{ maxHeight: 240, overflow: 'auto' }}>
                {fields.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No fields yet — tap the PDF.
                  </Typography>
                )}
                {fields.map((f) => (
                  <Chip
                    key={f.id}
                    size="small"
                    label={`p${f.pageIndex + 1} · ${labelForField(f, people)}`}
                    onClick={() => {
                      setSelectedId(f.id);
                      jumpToPage(f.pageIndex);
                    }}
                    onDelete={() => removeField(f.id)}
                    color={f.id === selectedId ? 'primary' : 'default'}
                    variant={f.id === selectedId ? 'filled' : 'outlined'}
                    sx={{
                      justifyContent: 'space-between',
                      maxWidth: '100%',
                      borderColor: colorForField(f, people),
                      '& .MuiChip-label': { maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis' }
                    }}
                  />
                ))}
              </Stack>
            </Collapse>
          </Box>
        </Stack>
      </DialogContent>

      {selected && (
        <Box
          sx={{
            px: { xs: 1.25, sm: 2 },
            py: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="body2" sx={{ flex: '1 1 120px', minWidth: 0 }} noWrap>
            Selected: <strong>{labelForField(selected, people)}</strong>
          </Typography>
          {(isMobile || selected.type === 'checkbox') && (
            <TextField
              size="small"
              label="Field label"
              value={selected.label || ''}
              onChange={(e) => updateSelected({ label: e.target.value })}
              placeholder={
                selected.type === 'checkbox'
                  ? 'e.g. I consent to electronic records'
                  : 'Label shown to signer'
              }
              sx={{ flex: '1 1 180px', minWidth: 140 }}
            />
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 40px)',
              gap: 0.5,
              justifyContent: 'center'
            }}
          >
            <span />
            <IconButton
              size="small"
              aria-label="Nudge up"
              onClick={() => nudgeSelected(0, 10)}
              sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
            >
              <ArrowUpIcon fontSize="small" />
            </IconButton>
            <span />
            <IconButton
              size="small"
              aria-label="Nudge left"
              onClick={() => nudgeSelected(-10, 0)}
              sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
            >
              <ArrowLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Nudge down"
              onClick={() => nudgeSelected(0, -10)}
              sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
            >
              <ArrowDownIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Nudge right"
              onClick={() => nudgeSelected(10, 0)}
              sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
            >
              <ArrowRightIcon fontSize="small" />
            </IconButton>
          </Box>
          <Button
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => removeField(selected.id)}
          >
            Remove
          </Button>
        </Box>
      )}

      <DialogActions
        sx={{
          px: { xs: 1.25, sm: 3 },
          py: { xs: 1.25, sm: 2 },
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: { xs: 'stretch', sm: 'flex-end' },
          '& > :not(style)': { xs: { flex: '1 1 40%' }, sm: { flex: '0 0 auto' } }
        }}
      >
        <Button onClick={requestClose} disabled={saving}>
          Close
        </Button>
        <Button
          variant="outlined"
          onClick={() => handleSave({ closeAfter: false })}
          disabled={saving || loading || !canSave || !isDirty}
        >
          {saving ? 'Saving…' : isDirty ? 'Save' : 'Saved'}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => handleSave({ closeAfter: true })}
          disabled={saving || loading || !canSave || fields.length === 0}
          sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
        >
          {saving ? 'Saving…' : 'Save & close'}
        </Button>
        {onRequestSignatures && (
          <Button
            variant="contained"
            disabled={fields.length === 0 || saving}
            onClick={handleContinue}
            sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          >
            Continue to send…
          </Button>
        )}
      </DialogActions>
      <Dialog open={closeConfirmOpen} onClose={() => setCloseConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save field changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            You moved or placed fields that are not saved yet. Closing now will lose those changes.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setCloseConfirmOpen(false)}>Keep editing</Button>
          <Button
            color="warning"
            onClick={() => {
              setCloseConfirmOpen(false);
              if (onClose) onClose();
            }}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            disabled={saving || !canSave}
            onClick={async () => {
              setCloseConfirmOpen(false);
              await handleSave({ closeAfter: true });
            }}
          >
            Save & close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PdfFieldPlacer;
